/**
 * PrivacyAcceptanceGate — the one-time acceptance prompt for EXISTING users who
 * signed up before the privacy policy existed, or before its latest edit. The
 * server flags them with `user.needsPrivacyAcceptance` (their stored version no
 * longer matches the current policy fingerprint) in the login/select response;
 * this blocks the app until they accept. New signups accept at account creation.
 *
 * Mounted once in Layout (the authenticated app shell). Renders nothing when the
 * flag is clear. The policy text is fetched from the server's single source
 * (useLegalPolicy) so it's always the current wording.
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { authApi } from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import MarkdownView from '../shared/MarkdownView';
import { usePrivacyPolicy } from '../../lib/useLegalPolicy';

export default function PrivacyAcceptanceGate() {
  const { user, updateUser, logout } = useAuthStore();
  const { policy, loading, error } = usePrivacyPolicy();
  const [accepting, setAccepting] = useState(false);

  if (!user?.needsPrivacyAcceptance) return null;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await authApi.acceptPrivacy();
      updateUser({ needsPrivacyAcceptance: false });
    } catch {
      toast.error('Could not record your acceptance. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-gate-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-7 py-5 border-b border-slate-200">
          <h2 id="privacy-gate-title" className="text-lg font-extrabold text-slate-900">
            Please review our Privacy Policy
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Before you continue, we need your agreement to how Avium hosts and handles your data
            {policy ? `. Effective ${policy.effectiveDate}.` : '.'}
          </p>
        </div>

        {/* Scrollable policy body */}
        <div className="flex-1 overflow-y-auto px-7 py-5">
          {loading && <p className="text-sm text-slate-400 animate-pulse">Loading the current policy…</p>}
          {error && (
            <p className="text-sm text-slate-600">
              We couldn't load the policy right now. Please refresh and try again.
            </p>
          )}
          {policy && <MarkdownView markdown={policy.markdown} />}
        </div>

        <div className="px-7 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={logout}
            className="text-sm font-semibold text-slate-500 hover:text-slate-700 bg-transparent border-0 cursor-pointer p-0"
          >
            Not now — sign out
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting || !policy}
            className="px-6 py-2.5 bg-brand-primary text-white rounded-lg text-sm font-bold cursor-pointer transition-all hover:bg-brand-primary-dark active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {accepting ? 'Saving…' : 'I Agree & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
